import { BadInputError } from '@watershed/errors/BadInputError';
import { ASTCall, stringifyExpr } from '@watershed/lscript/ast';
import {
  ExprType,
  isNonNullable,
  makeNonNullable,
  stringifyType,
} from '@watershed/lscript/expr_types';
import {
  assertArgShape,
  assertNoKwargs,
  typecheckExpression,
  TypecheckWarning,
} from '../typecheck';
import { Context, Func, SqlExpression, SqlTrace } from '../types';
import { safeMaxStage } from '../utils';
import { traceHelper } from '../sql/internal';
import {
  RawSQL,
  /* eslint-disable @watershed/no-dangerous-raw-sql -- DuckDB usage only */
  dangerousRawSql,
  sql,
  sqlF,
  sqlJoinF,
} from '@watershed/lscript/sqlTemplate';
import { bestCompatibleType } from '../utils/bestCompatibleType';
import { TypeConversionError, convertToType } from '../typeConversion';

export const coalesce: Func = {
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings?: Array<TypecheckWarning>
  ): ExprType {
    assertArgShape(call, 0);
    assertNoKwargs(call);

    const typedArgs = call.args.map((expr) => ({
      ty: typecheckExpression(ctx, expr, warnings),
      expr,
    }));
    const outType = typedArgs
      .map((a) => a.ty)
      .reduce((a, b) => bestCompatibleType(a, b));

    // Confirm that each argument could be converted to the output type.
    for (const { ty, expr } of typedArgs) {
      try {
        convertToType(ctx, { fromExpr: expr, toType: outType });
      } catch (error: any) {
        if (error instanceof TypeConversionError) {
          throw new BadInputError(
            `Cannot coalesce arguments with incompatible types: tried to convert all arguments to type \`${stringifyType(
              outType
            )}\`, but conversion failed for the argument \`${stringifyExpr(
              expr
            )}\` (of type \`${stringifyType(ty)}\`)`
          );
        }
        throw error;
      }
    }

    // If any of the args are non-nullable, the result of coalesce is also
    // non-nullable.
    const hasNonNull = typedArgs.some(({ ty }) => isNonNullable(ty));
    return hasNonNull ? makeNonNullable(outType) : outType;
  },

  eval(ctx: Context, call: ASTCall): unknown {
    const argTypes = call.args.map((expr) => typecheckExpression(ctx, expr));
    const outType = argTypes.reduce((a, b) => bestCompatibleType(a, b));
    const conversions = call.args.map((expr) =>
      convertToType(ctx, { fromExpr: expr, toType: outType })
    );
    for (const conv of conversions) {
      const value = conv.eval();
      if (value !== null) {
        return value;
      }
    }
    return null;
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    const argTypes = call.args.map((expr) => typecheckExpression(ctx, expr));
    const outType = argTypes.reduce((a, b) => bestCompatibleType(a, b));
    const conversions = call.args.map((expr) =>
      convertToType(ctx, { fromExpr: expr, toType: outType })
    );
    const eArgs = conversions.map((conv) => conv.toSql());

    // Coalesce does nothing if it's passed a single argument, and getting this
    // case out of the way early simplifies some of our codegen later (e.g.,
    // `CASE ELSE 4 END` is a syntax error--you need at least one `WHEN`)
    if (eArgs.length === 1) {
      return eArgs[0];
    }

    const traceArms = new Array<
      (sub: (e: SqlTrace | null) => RawSQL) => RawSQL
    >();
    const unitArms = new Array<RawSQL>();
    const errorExistsArms = new Array<RawSQL>();
    const errorExprArms = new Array<RawSQL>();
    for (let i = 0; i < eArgs.length; i++) {
      const eArg = eArgs[i];
      const exprIsNotNullF = sqlF`WHEN (${dangerousRawSql(
        eArg.expr
      )} IS NOT NULL)`;
      const unitValueSqlF: RawSQL = !eArg.unitExpr
        ? sqlF`CAST(NULL AS VARCHAR)`
        : sqlF`CAST(${dangerousRawSql(eArg.unitExpr)} AS VARCHAR)`;
      const errorExistsSqlF: RawSQL = !eArg.error?.exists
        ? sqlF`FALSE`
        : sqlF`CAST(${dangerousRawSql(eArg.error.exists)} AS BOOLEAN)`;
      const errorExprSqlF: RawSQL = !eArg.error?.expr
        ? sqlF`CAST(NULL AS VARCHAR)`
        : sqlF`CAST(${dangerousRawSql(eArg.error.expr)} AS VARCHAR)`;
      if (i === eArgs.length - 1) {
        // Last arm is taken regardless of what it evaluates to
        traceArms.push((sub) => sqlF`ELSE ${sub(eArg.trace)}`);
        unitArms.push(sqlF`ELSE ${unitValueSqlF}`);
        errorExistsArms.push(sqlF`ELSE ${errorExistsSqlF}`);
        errorExprArms.push(sqlF`ELSE ${errorExprSqlF}`);
      } else {
        traceArms.push(
          (sub) => sqlF`${exprIsNotNullF} THEN ${sub(eArg.trace)}`
        );
        unitArms.push(sqlF`${exprIsNotNullF} THEN ${unitValueSqlF}`);
        errorExistsArms.push(
          sqlF`${exprIsNotNullF} OR (${errorExistsSqlF}) THEN (${errorExistsSqlF})`
        );
        errorExprArms.push(
          sqlF`${exprIsNotNullF} OR (${errorExistsSqlF}) THEN ${errorExprSqlF}`
        );
      }
    }

    let unitExpr: null | string = null;
    if (
      (outType.ty === 'nullable' && outType.baseTy.ty === 'quantity') ||
      outType.ty === 'quantity'
    ) {
      unitExpr = sql`CASE ${sqlJoinF(unitArms, ' ')} END`;
    }

    return traceHelper(
      outType,
      {
        expr: `COALESCE(${eArgs.map((a) => a.expr).join(', ')})`,
        stage: safeMaxStage(eArgs, 0),
        unitExpr,
        error: {
          exists: sql`CASE ${sqlJoinF(errorExistsArms, ' ')} END`,
          expr: sql`CASE ${sqlJoinF(errorExprArms, ' ')} END`,
        },
      },
      (sub) =>
        sqlF`CASE ${sqlJoinF(
          traceArms.map((a) => a(sub)),
          ' '
        )} END`
    );
  },
};
